/**
 * Schémata pro consent flow (UC-009 + DEC-013 Policy C).
 *
 * Plain TypeScript validation — žádné runtime knihovny (Zod přijde v pozdější
 * fázi). Funkce vrací výsledek + error message; component logika to mapuje
 * na UI state.
 */

export const CONSENT_TYPES = [
  "data_processing",
  "data_sharing_axigon",
  "push_assist_paper",
  "marketing_optional",
] as const;

export type ConsentType = (typeof CONSENT_TYPES)[number];

/** Lidsky čitelné popisky souhlasů (cs) — UI místo strojových klíčů. */
export const CONSENT_TYPE_LABELS: Record<ConsentType, string> = {
  data_processing: "Zpracování osobních údajů",
  data_sharing_axigon: "Sdílení dat se systémem Axigon",
  push_assist_paper: "Papírové doručení (push-assist)",
  marketing_optional: "Marketingová sdělení (volitelné)",
};

export const CONSENT_TYPE_DESCRIPTIONS: Record<ConsentType, string> = {
  data_processing:
    "Zpracování vašich osobních údajů pro účely refakturace tankování.",
  data_sharing_axigon:
    "Vaše tankovací data se čtou ze systému Axigon (jen pro čtení).",
  push_assist_paper:
    "QR k úhradě může být doručen i papírovou formou.",
  marketing_optional:
    "Občasná nepovinná sdělení. Lze kdykoli odvolat.",
};

export interface ConsentAcceptPayload {
  consent_text_version: string;
  consent_types: ConsentType[];
}

export interface ConsentWithdrawPayload {
  consent_type: ConsentType;
  consent_text_version: string;
  withdrawal_reason: string;
}

export interface ValidationResult<T> {
  ok: boolean;
  value?: T;
  error?: string;
}

/**
 * Validace payloadu pro POST /api/tenant/consent/accept.
 *
 * - consent_text_version je neprázdný string odpovídající ISO date pattern
 *   "YYYY-MM-DD" (optional `_vN` nebo `_cosmetic` suffix).
 * - consent_types je neprázdné pole povolených enum hodnot, bez duplicit.
 */
export function validateAcceptPayload(
  raw: unknown,
): ValidationResult<ConsentAcceptPayload> {
  if (!raw || typeof raw !== "object") {
    return { ok: false, error: "Payload musí být objekt." };
  }
  const obj = raw as Record<string, unknown>;
  const version = obj.consent_text_version;
  if (typeof version !== "string" || !VERSION_REGEX.test(version)) {
    return {
      ok: false,
      error: "consent_text_version musí být ISO formát 'YYYY-MM-DD' (volitelně suffix).",
    };
  }
  const types = obj.consent_types;
  if (!Array.isArray(types) || types.length === 0) {
    return { ok: false, error: "consent_types musí být neprázdné pole." };
  }
  const seen = new Set<string>();
  const validated: ConsentType[] = [];
  for (const t of types) {
    if (typeof t !== "string") {
      return { ok: false, error: "consent_types obsahuje non-string položku." };
    }
    if (!isConsentType(t)) {
      return { ok: false, error: `Neznámý consent_type: '${t}'` };
    }
    if (seen.has(t)) {
      return { ok: false, error: `Duplicitní consent_type: '${t}'` };
    }
    seen.add(t);
    validated.push(t);
  }
  return {
    ok: true,
    value: { consent_text_version: version, consent_types: validated },
  };
}

export function validateWithdrawPayload(
  raw: unknown,
): ValidationResult<ConsentWithdrawPayload> {
  if (!raw || typeof raw !== "object") {
    return { ok: false, error: "Payload musí být objekt." };
  }
  const obj = raw as Record<string, unknown>;
  if (
    typeof obj.consent_type !== "string" ||
    !isConsentType(obj.consent_type)
  ) {
    return { ok: false, error: "consent_type musí být validní enum." };
  }
  const version = obj.consent_text_version;
  if (typeof version !== "string" || !VERSION_REGEX.test(version)) {
    return {
      ok: false,
      error: "consent_text_version musí být ISO formát.",
    };
  }
  const reason = obj.withdrawal_reason;
  if (typeof reason !== "string" || reason.trim().length === 0) {
    return {
      ok: false,
      error: "withdrawal_reason je povinný (DB CHECK consent_withdrawal_reason_required).",
    };
  }
  return {
    ok: true,
    value: {
      consent_type: obj.consent_type,
      consent_text_version: version,
      withdrawal_reason: reason.trim(),
    },
  };
}

export function isConsentType(value: string): value is ConsentType {
  return (CONSENT_TYPES as readonly string[]).includes(value);
}

// Acceptable: "2026-05-13", "2026-05-13_v2", "2026-05-13_cosmetic", "2026-01-01"
const VERSION_REGEX = /^\d{4}-\d{2}-\d{2}(_[a-z0-9]+)?$/;
